import { useCallback, useEffect, useMemo, useState } from 'react';
import { AlertTriangle, Loader2, PackageCheck, PackageX, RotateCcw, ShieldAlert } from 'lucide-react';

import ShopSelect from '../../../components/ShopSelect';
import { businessDeliveryOperationsService as deliveryApi } from '../../services/businessDeliveryOperationsService';

// WP7 receiving desk (target-v2, dark).
//
// Three things happen at this counter, and they are deliberately three separate
// decisions rather than one "handle return" button:
//
//   1. Receipt. Staff confirm the Parcels they physically took. This is the only
//      command in WP7 that moves custody, and it moves it for the scanned
//      Parcels alone - anything not ticked stays with the Rider and stays
//      visible. There is no "receive all" shortcut, because the point of the
//      scan is that somebody looked at each item.
//   2. Disposition. What happens to a Parcel now that it is back. A Shipment
//      gets one automatic re-admission; after that the screen stops offering it.
//   3. Cancellations raised after pickup. Approving one stops the handover and
//      nothing else - the Parcel still has to come back through (1).

const RETENTION_REASONS = Object.freeze([
  ['NOT_PRESENTED', 'The rider did not bring it'],
  ['DAMAGED', 'Damaged - not accepting it here'],
  ['LABEL_UNREADABLE', 'Label cannot be read'],
  ['STAFF_REFUSED', 'Refusing it for another reason'],
]);

const DISPOSITIONS = Object.freeze([
  ['REDISPATCH', 'Send it out again'],
  ['RESTOCK', 'Take it back into stock'],
  ['MANAGEMENT_REVIEW', 'Escalate for management review'],
  ['WRITE_OFF', 'Write it off'],
]);

// What each choice does to the order, said before it is chosen (APP-DEL-029).
const OUTCOME_HINT = Object.freeze({
  REDISPATCH: 'The order stays open and dispatch plans a new delivery.',
  RESTOCK: 'The order is cancelled and its items go back into sellable stock. Unpaid cash is no longer expected; a prepaid order is refunded to the shopper\'s Apparify Wallet.',
  WRITE_OFF: 'The order is cancelled and its items do not go back into stock. Unpaid cash is no longer expected; a prepaid order is refunded to the shopper\'s Apparify Wallet.',
  MANAGEMENT_REVIEW: 'The order stays open until a manager decides. The parcel stays out of dispatch.',
});

// The desk's confirmation, from what the server actually did.
export function decisionNotice(code, result = {}) {
  if (code === 'REDISPATCH') return 'Re-admitted. Dispatch will plan it again as new work.';
  if (code === 'MANAGEMENT_REVIEW') return "Escalated. It waits here for a manager's decision; the order stays open.";
  const items = (result.stockReturned || []).reduce((sum, line) => sum + Number(line.quantity || 0), 0);
  const parts = [code === 'RESTOCK' ? 'Put back in stock.' : 'Written off.'];
  if (result.orderClosed) parts.push('The order is cancelled.');
  if (items) parts.push(`${items} item${items === 1 ? '' : 's'} returned to stock.`);
  if (result.codVoided) parts.push('The unpaid cash-on-delivery amount is no longer expected.');
  if (result.refund) parts.push("The shopper was refunded to their Apparify Wallet.");
  return parts.join(' ');
}

const ARRIVAL_LABEL = Object.freeze({
  RETURNED_TO_BUSINESS: 'Returned after a failed delivery',
  RECOVERED_BY_BUSINESS: 'Recovered - the rider could not continue',
});

function errorMessage(error, fallback) {
  const status = error?.response?.status;
  if (status === 409) {
    return error?.response?.data?.message
      || 'This changed while you were working on it. Refresh and check the latest state.';
  }
  if (status === 403) return 'You do not have permission to do that here.';
  return error?.response?.data?.message || fallback;
}

function ReceiptCard({ session, busy, onConfirm }) {
  const [outcomes, setOutcomes] = useState(() => new Map());
  const expected = session.expectedParcels || [];

  const setOutcome = useCallback((shipmentId, value) => {
    setOutcomes((current) => {
      const next = new Map(current);
      if (value === null) next.delete(shipmentId);
      else next.set(shipmentId, value);
      return next;
    });
  }, []);

  const decided = expected.filter((parcel) => outcomes.has(parcel.shipmentId));
  const received = decided.filter((parcel) => outcomes.get(parcel.shipmentId) === 'RECEIVED');

  return (
    <article className="rounded-2xl border border-neutral-200 p-4 dark:border-neutral-700">
      <header>
        <h3 className="font-semibold">
          {session.riderName || `Rider ${session.riderBusinessMemberId}`}
        </h3>
        <p className="text-sm text-neutral-500">
          {session.sessionKind === 'EMERGENCY_RECOVERY'
            ? 'Emergency handover - the rider cannot continue'
            : 'Returning parcels from a trip'}
          {' - '}
          {session.parcelCount} presented
        </p>
      </header>

      <ul className="mt-3 space-y-2">
        {expected.map((parcel) => {
          const outcome = outcomes.get(parcel.shipmentId) || '';
          return (
            <li key={parcel.shipmentId} className="rounded-xl bg-neutral-50 p-3 dark:bg-white/5">
              <p className="font-medium">{parcel.orderReference || `Parcel ${parcel.shipmentId}`}</p>
              <div className="mt-2 flex flex-wrap items-center gap-2">
                <button
                  type="button"
                  aria-pressed={outcome === 'RECEIVED'}
                  onClick={() => setOutcome(parcel.shipmentId, outcome === 'RECEIVED' ? null : 'RECEIVED')}
                  className={`min-h-11 rounded-xl px-3 text-sm font-semibold ${
                    outcome === 'RECEIVED'
                      ? 'bg-accent text-on-accent'
                      : 'border border-neutral-300 dark:border-neutral-600'
                  }`}
                >
                  I took this one
                </button>
                <div className="min-w-0 text-sm">
                  <span className="sr-only">
                    Why {parcel.orderReference || parcel.shipmentId} stayed with the rider
                  </span>
                  <ShopSelect
                    value={outcome === 'RECEIVED' ? '' : outcome}
                    onValueChange={(value) => setOutcome(
                      parcel.shipmentId, value || null,
                    )}
                    options={[
                      { value: '', label: 'Stayed with the rider because...' },
                      ...RETENTION_REASONS.map(([value, label]) => ({ value, label })),
                    ]}
                    ariaLabel={`Why ${parcel.orderReference || parcel.shipmentId} stayed with the rider`}
                    align="start"
                    minPanelWidth={288}
                    triggerClassName="min-h-11 w-72 rounded-control px-3 text-sm font-normal"
                    panelClassName="py-1"
                  />
                </div>
              </div>
            </li>
          );
        })}
      </ul>

      <p className="mt-3 text-sm text-neutral-600 dark:text-neutral-300">
        Only what you tick moves into the business. Anything else stays counted
        against the rider.
      </p>

      <button
        type="button"
        disabled={busy || decided.length === 0}
        onClick={() => onConfirm(session, expected
          .filter((parcel) => outcomes.has(parcel.shipmentId))
          .map((parcel) => (outcomes.get(parcel.shipmentId) === 'RECEIVED'
            ? { shipmentId: parcel.shipmentId, eventType: 'RETURNED_TO_BUSINESS', scanMethod: 'VISUAL' }
            : {
              shipmentId: parcel.shipmentId,
              eventType: 'RETAINED_BY_RIDER',
              reasonCode: outcomes.get(parcel.shipmentId),
            })))}
        className="business-primary-action mt-3 flex min-h-11 items-center gap-2 rounded-xl bg-accent px-4 text-sm font-semibold text-on-accent hover:bg-accent-hover active:bg-accent-active focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-focus disabled:opacity-50"
      >
        {busy ? <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
          : <PackageCheck className="h-4 w-4" aria-hidden="true" />}
        Confirm receipt of {received.length} of {expected.length}
      </button>
    </article>
  );
}

function DispositionCard({ parcel, busy, onDecide }) {
  const [code, setCode] = useState('REDISPATCH');
  const [justification, setJustification] = useState('');
  const tooShort = justification.trim().length < 10;
  const management = Boolean(parcel.awaitingManagement);
  // A manager decides the final outcome, and may send it out again even past
  // the one automatic re-admission.
  const options = management
    ? DISPOSITIONS.filter(([value]) => value !== 'MANAGEMENT_REVIEW')
    : parcel.readmissionAvailable
      ? DISPOSITIONS
      : DISPOSITIONS.filter(([value]) => value !== 'REDISPATCH');
  const chosen = options.some(([value]) => value === code) ? code : options[0][0];

  return (
    <article className="rounded-2xl border border-neutral-200 p-4 dark:border-neutral-700">
      <h3 className="font-semibold">{parcel.orderReference || `Parcel ${parcel.shipmentId}`}</h3>
      <p className="text-sm text-neutral-500">
        {ARRIVAL_LABEL[parcel.arrivedBy] || 'Back at the counter'}
      </p>
      {management ? (
        <p className="mt-2 flex items-start gap-2 rounded-xl border border-info bg-info-subtle p-3 text-sm text-info" data-testid="awaiting-management">
          <ShieldAlert className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" />
          Waiting for a manager&apos;s decision. The order stays open and the parcel stays
          out of dispatch until a manager decides below.
        </p>
      ) : parcel.readmissionAvailable ? null : (
        <p className="mt-2 flex items-start gap-2 rounded-xl bg-warning-subtle p-3 text-sm text-warning">
          <ShieldAlert className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" />
          This parcel has already used its one automatic second attempt. Put it back in
          stock, write it off, or escalate it so a manager can decide.
        </p>
      )}

      <div className="mt-3 block text-sm font-semibold">
        <span>{management ? 'Management decision' : 'What happens to it'}</span>
        <ShopSelect
          value={chosen}
          onValueChange={setCode}
          options={options.map(([value, label]) => ({ value, label }))}
          ariaLabel={management ? 'Management decision' : 'What happens to it'}
          align="start"
          minPanelWidth={240}
          triggerClassName="mt-1 min-h-11 w-full rounded-control px-3 text-sm font-normal"
          panelClassName="py-1"
        />
      </div>
      <p className="mt-1 text-xs text-neutral-500">{OUTCOME_HINT[chosen]}</p>

      <label className="mt-3 block text-sm font-semibold">
        Why
        <textarea
          value={justification}
          onChange={(event) => setJustification(event.target.value)}
          rows={2}
          maxLength={1000}
          className="mt-1 w-full rounded-xl border border-neutral-300 bg-white px-3 py-2 font-normal dark:border-neutral-600 dark:bg-neutral-900"
        />
      </label>

      <button
        type="button"
        disabled={busy || tooShort}
        onClick={() => onDecide(parcel, {
          dispositionCode: chosen,
          justification: justification.trim(),
          expectedShipmentVersion: parcel.shipmentVersion,
        })}
        className="business-primary-action mt-3 flex min-h-11 items-center gap-2 rounded-xl bg-accent px-4 text-sm font-semibold text-on-accent hover:bg-accent-hover active:bg-accent-active focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-focus disabled:opacity-50"
      >
        {busy ? <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
          : <RotateCcw className="h-4 w-4" aria-hidden="true" />}
        {management ? 'Record the management decision' : 'Record this decision'}
      </button>
    </article>
  );
}

function CancellationCard({ request, busy, onDecide }) {
  const [outcome, setOutcome] = useState('RETURN_REQUIRED');
  const [reason, setReason] = useState('');
  const tooShort = reason.trim().length < 10;

  return (
    <article className="rounded-2xl border border-neutral-200 p-4 dark:border-neutral-700">
      <h3 className="font-semibold">
        {request.orderReference || `Order ${request.orderId}`}
      </h3>
      <p className="text-sm text-neutral-500">
        {request.requestedByName || 'The customer'} asked to cancel after the parcel
        left with the rider.
      </p>
      <p className="mt-2 rounded-xl bg-neutral-50 p-3 text-sm dark:bg-white/5">
        &ldquo;{request.requestReason}&rdquo;
      </p>
      <p className="mt-2 text-sm text-neutral-600 dark:text-neutral-300">
        Approving stops the handover. The parcel stays with the rider until it comes
        back through the counter, and any cash they collected follows its own rules.
      </p>

      <div className="mt-3 block text-sm font-semibold">
        <span>If you approve, what happens next</span>
        <ShopSelect
          value={outcome}
          onValueChange={setOutcome}
          options={[
            { value: 'RETURN_REQUIRED', label: 'The rider brings it back' },
            { value: 'MANAGEMENT_REVIEW', label: 'Management decides' },
          ]}
          ariaLabel="If you approve, what happens next"
          align="start"
          minPanelWidth={240}
          triggerClassName="mt-1 min-h-11 w-full rounded-control px-3 text-sm font-normal"
          panelClassName="py-1"
        />
      </div>

      <label className="mt-3 block text-sm font-semibold">
        Reason for the record
        <textarea
          value={reason}
          onChange={(event) => setReason(event.target.value)}
          rows={2}
          maxLength={1000}
          className="mt-1 w-full rounded-xl border border-neutral-300 bg-white px-3 py-2 font-normal dark:border-neutral-600 dark:bg-neutral-900"
        />
      </label>

      <div className="mt-3 flex flex-wrap gap-2">
        <button
          type="button"
          disabled={busy || tooShort}
          onClick={() => onDecide(request, {
            decision: 'APPROVED', outcome, decisionReason: reason.trim(),
          })}
          className="business-primary-action min-h-11 rounded-xl bg-accent px-4 text-sm font-semibold text-on-accent hover:bg-accent-hover active:bg-accent-active focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-focus disabled:opacity-50"
        >
          Stop the delivery
        </button>
        <button
          type="button"
          disabled={busy || tooShort}
          onClick={() => onDecide(request, {
            decision: 'DECLINED', decisionReason: reason.trim(),
          })}
          className="min-h-11 rounded-xl border border-neutral-300 px-4 text-sm font-semibold disabled:opacity-50 dark:border-neutral-600"
        >
          Let the delivery continue
        </button>
      </div>
    </article>
  );
}

export default function ReturnsDesk({ businessId, canReceive = true, canDecide = true, canReviewCancellations = true }) {
  const [sessions, setSessions] = useState([]);
  const [parcels, setParcels] = useState([]);
  const [cancellations, setCancellations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [busy, setBusy] = useState('');
  const [revision, setRevision] = useState(0);

  useEffect(() => {
    if (!businessId) return undefined;
    const controller = new AbortController();
    setLoading(true);
    const requests = [
      canReceive && deliveryApi.returnSessions(businessId, { signal: controller.signal }),
      canDecide && deliveryApi.returnedParcels(businessId, { signal: controller.signal }),
      canReviewCancellations && deliveryApi.postCustodyCancellations(businessId, { status: 'REQUESTED' }, { signal: controller.signal }),
    ];
    Promise.allSettled(requests).then(([sessionResult, parcelResult, cancellationResult]) => {
      if (controller.signal.aborted) return;
      setSessions(sessionResult.status === 'fulfilled' ? sessionResult.value?.items || [] : []);
      setParcels(parcelResult.status === 'fulfilled' ? parcelResult.value?.items || [] : []);
      setCancellations(cancellationResult.status === 'fulfilled' ? cancellationResult.value?.items || [] : []);
      setError([sessionResult, parcelResult, cancellationResult].some((result) => result.status === 'rejected')
        ? 'Some returns could not be loaded. Refresh and check the desk again.' : '');
    }).finally(() => {
      if (!controller.signal.aborted) setLoading(false);
    });
    return () => controller.abort();
  }, [businessId, canReceive, canDecide, canReviewCancellations, revision]);

  const act = useCallback(async (key, work, successMessage) => {
    setBusy(key);
    setError('');
    setNotice('');
    try {
      const result = await work();
      setNotice(typeof successMessage === 'function' ? successMessage(result) : successMessage);
      setRevision((current) => current + 1);
    } catch (requestError) {
      setError(errorMessage(requestError, 'That could not be recorded. Try again.'));
    } finally {
      setBusy('');
    }
  }, []);

  const empty = useMemo(
    () => !sessions.length && !parcels.length && !cancellations.length,
    [sessions, parcels, cancellations],
  );

  if (loading) {
    return (
      <section className="rounded-3xl border border-neutral-200 bg-white p-5 dark:border-neutral-800 dark:bg-neutral-900">
        <p className="flex items-center gap-2 text-sm text-neutral-500">
          <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
          Loading the returns desk...
        </p>
      </section>
    );
  }

  return (
    <section className="space-y-4">
      {error ? (
        <p role="alert" className="flex items-start gap-2 rounded-2xl bg-danger-subtle p-4 text-sm text-danger">
          <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" />
          {error}
        </p>
      ) : null}
      {notice ? (
        <p role="status" className="rounded-2xl bg-success-subtle p-4 text-sm text-success">
          {notice}
        </p>
      ) : null}

      {empty ? (
        <div className="flex items-start gap-3 rounded-2xl border border-neutral-200 bg-white p-4 dark:border-neutral-800 dark:bg-neutral-950">
          <PackageX className="mt-0.5 h-5 w-5 shrink-0 text-neutral-400" aria-hidden="true" />
          <div>
          <h2 className="font-semibold">Nothing waiting at the counter</h2>
          <p className="mt-1 text-sm text-neutral-600 dark:text-neutral-300">
            Parcels a rider brings back, decisions those parcels are waiting on, and
            cancellations raised after pickup all appear here.
          </p>
          </div>
        </div>
      ) : null}

      {sessions.length ? (
        <div className="rounded-3xl border border-neutral-200 bg-white p-5 dark:border-neutral-800 dark:bg-neutral-900">
          <h2 className="font-semibold">Riders at the counter</h2>
          <p className="mt-1 text-sm text-neutral-500">
            Confirm only what you physically took.
          </p>
          <div className="mt-4 grid gap-3 lg:grid-cols-2">
            {sessions.map((session) => (
              <ReceiptCard
                key={session.returnSessionId}
                session={session}
                busy={busy === `receipt:${session.returnSessionId}`}
                onConfirm={(target, confirmedParcels) => act(
                  `receipt:${target.returnSessionId}`,
                  () => deliveryApi.confirmReturnReceipt(businessId, target.returnSessionId, {
                    expectedVersion: target.version, parcels: confirmedParcels,
                  }),
                  'Receipt recorded. Only the parcels you confirmed moved into the business.',
                )}
              />
            ))}
          </div>
        </div>
      ) : null}

      {parcels.length ? (
        <div className="rounded-3xl border border-neutral-200 bg-white p-5 dark:border-neutral-800 dark:bg-neutral-900">
          <h2 className="font-semibold">Parcels waiting on a decision</h2>
          <p className="mt-1 text-sm text-neutral-500">
            These are held out of dispatch until somebody decides what happens to them.
          </p>
          <div className="mt-4 grid gap-3 lg:grid-cols-2">
            {parcels.map((parcel) => (
              <DispositionCard
                key={parcel.returnFactId}
                parcel={parcel}
                busy={busy === `disposition:${parcel.shipmentId}`}
                onDecide={(target, input) => act(
                  `disposition:${target.shipmentId}`,
                  () => (target.awaitingManagement
                    ? deliveryApi.decideReturnAfterManagementReview(businessId, target.shipmentId, input)
                    : deliveryApi.decideReturnDisposition(businessId, target.shipmentId, input)),
                  (result) => decisionNotice(input.dispositionCode, result),
                )}
              />
            ))}
          </div>
        </div>
      ) : null}

      {cancellations.length ? (
        <div className="rounded-3xl border border-neutral-200 bg-white p-5 dark:border-neutral-800 dark:bg-neutral-900">
          <h2 className="font-semibold">Cancellations raised after pickup</h2>
          <p className="mt-1 text-sm text-neutral-500">
            A customer asked to cancel after the parcel was already with the rider.
          </p>
          <div className="mt-4 grid gap-3 lg:grid-cols-2">
            {cancellations.map((request) => (
              <CancellationCard
                key={request.cancellationId}
                request={request}
                busy={busy === `cancellation:${request.cancellationId}`}
                onDecide={(target, input) => act(
                  `cancellation:${target.cancellationId}`,
                  () => deliveryApi.decidePostCustodyCancellation(
                    businessId, target.cancellationId, input,
                  ),
                  input.decision === 'APPROVED'
                    ? 'Delivery stopped. The parcel still has to come back through the counter.'
                    : 'Declined. The delivery continues.',
                )}
              />
            ))}
          </div>
        </div>
      ) : null}
    </section>
  );
}
